/**
 * LandingParent — /parent route.
 * Faithful port of parent.html from the AI-IDE design.
 *
 * CTA: "Try Now" → /auth/parent (B2C parent auth)
 */
import { Link } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import ContactSection from '@/components/landing/ContactSection';
import { LANDING_CTA, PAGE_SOURCE } from '@/lib/landingCTAs';

const CHECK_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-purple)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 3, flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PARENT_FEATURES = [
  {
    img: '/landing-v2/assets/parent_dashboard.webp',
    title: 'Topic Mastery Map',
    body: 'Daily scores per concept — not just overall percentages. Know exactly where your child stands in Physics, Math, and more.',
  },
  {
    img: '/landing-v2/assets/parent_transcript.webp',
    title: 'Full Chat Transcripts',
    body: 'Read every exchange between your child and the AI. See how they reason — not just what score they got.',
  },
  {
    img: '/landing-v2/assets/parent_alert.webp',
    title: 'Smart Alerts',
    body: 'Notified when a concept gap appears — not weeks later at a parent-teacher meeting. Act early, not after the fact.',
  },
];

const PARENT_BULLETS = [
  {
    title: 'Real-Time Mastery Dashboard',
    desc: 'Track concept-by-concept understanding rather than just broad, late-coming test marks.',
  },
  {
    title: 'Interactive Chat Transcripts',
    desc: 'See exact conversation records to understand the unique way your child approaches hard problems.',
  },
  {
    title: 'Remediation & Support Alerts',
    desc: 'Know immediately if your child needs extra help or is ready for advanced acceleration.',
  },
];

export default function LandingParent() {
  return (
    <LandingShell activeNav="parent">
      {/* ============== HERO ============== */}
      <section id="hero" style={{ padding: '130px 0 24px 0' }}>
        <div className="container text-center">
          <div className="nep-badge" style={{ margin: '0 auto 16px auto' }}>
            <span className="pulse-dot" /> FOR PARENTS &amp; GUARDIANS
          </div>
          <h1 style={{ fontSize: '3rem', marginBottom: 12 }}>
            No More Waiting<br />
            <span className="gradient-text">for the Report Card.</span>
          </h1>
          <p className="hero-subtitle" style={{ margin: '0 auto', maxWidth: 520, fontSize: '1.05rem' }}>
            See what your child understands — and what they don&apos;t — every
            single day. Not just on exam day.
          </p>
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to={LANDING_CTA.TRY_NOW_PARENT}
              className="btn btn-primary btn-lg glow-btn"
              data-testid="parent-try-now"
            >
              Try Now
            </Link>
          </div>
        </div>
      </section>

      {/* ============== THREE PILLARS ============== */}
      <section className="section-padding light-bg" style={{ padding: '48px 0' }}>
        <div className="container">
          <div className="section-header text-center" style={{ marginBottom: 32 }}>
            <span className="section-tag">WHAT YOU SEE</span>
            <h2>Full Clarity. Zero Surprises.</h2>
          </div>
          <div className="philosophy-grid">
            {PARENT_FEATURES.map((f) => (
              <div className="philosophy-card text-center" key={f.title}>
                <img
                  src={f.img}
                  alt={f.title}
                  style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 12, marginBottom: 20 }}
                />
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PORTAL OVERVIEW ============== */}
      <section id="demo" className="section-padding" style={{ padding: '64px 0 80px 0' }}>
        <div className="container">
          <div className="panel-layout">
            <div className="vertical-pitch">
              <div className="pitch-tag text-purple">PARENT PORTAL</div>
              <h3>A Window Into Every Session</h3>
              <p className="pitch-lead">
                Yukti keeps you seamlessly connected with your child&apos;s
                education. Get actual visibility into how your child reasons and
                learns, without hover-parenting.
              </p>
              <ul
                className="pitch-bullet-list"
                style={{ marginTop: 16, listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                {PARENT_BULLETS.map((b) => (
                  <li key={b.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {CHECK_ICON}
                    <div>
                      <strong style={{ display: 'block', color: 'var(--text-primary)', marginBottom: 2 }}>
                        {b.title}
                      </strong>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                        {b.desc}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="showcase-card glass"
              style={{
                overflow: 'hidden', borderRadius: 20,
                border: '1px solid var(--border-color)',
                boxShadow: 'var(--shadow-card)',
                backgroundColor: 'var(--bg-tertiary)',
              }}
            >
              <img
                src="/landing-v2/assets/confident_parent.webp"
                alt="Parent reviewing child's learning progress on Yukti"
                style={{ width: '100%', height: 340, objectFit: 'cover', display: 'block' }}
              />
              <div
                className="showcase-caption"
                style={{ padding: 24, background: 'var(--bg-primary)', borderTop: '1px solid var(--border-color)' }}
              >
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', color: 'var(--text-primary)' }}>
                  Informed every day
                </h4>
                <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Kiran reviews Aarav&apos;s real-time conceptual mastery scores
                  before the parent-teacher meeting — ensuring zero surprises.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactSection sourcePage={PAGE_SOURCE.PARENT} />
    </LandingShell>
  );
}
